import { GameListType } from "./GameList"
import { findGame, isMobile, safeCreateUrl } from "./Utils"

type Parameter = {
    backendName: string,
    frontendName?: string,
    required?: boolean
    specialParsing?: (value?: string, gameList?: GameListType, urlParams?: URLSearchParams) => string
}

// 1. if just backendName is provided, frontendName is the same as backendName, and the url value is passed as is
// 2. if frontendName is provided, the url value is passed as is, but with differentName, for example, backend sends gameId=123, frontend receives gameID=123
// 3. if specialParsing is provided, the url value is passed to the function, and the result is used as the parameter value
// 4. if required is provided, the parameter is required, and an error is thrown if it is not provided
// 5. check ReturnFrontendParameters below for implementation details
const E3Parameters: Parameter[] = [
    {
        backendName: "gameId",
        frontendName: "gameName",
        required: true,
        specialParsing: (value: string, gameList: GameListType) => {
            const gameName = findGame(value, gameList).name
            const gameVersion = checkIfMobile() ? "_mobile" : "_web"
            return gameName + gameVersion
        }
    },
    {
        backendName: "userId",
        frontendName: "playerID",
        required: true
    },
    {
        backendName: "sessionId",
        frontendName: "sessionID",
        required: true
    },
    {
        backendName: "gameServerAddress",
        frontendName: "secureConnection",
        specialParsing: (value: string) => String(safeCreateUrl(value).protocol !== "http:")
    },
    {
        backendName: "gameServerAddress",
        frontendName: "host",
        required: true,
        specialParsing: (value: string) => {
            const host = safeCreateUrl(value)
            return host.hostname + host.pathname
        }
    },
    {
        backendName: "integrations",
        frontendName: "returnURL",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "language",
        frontendName: "languages",
        specialParsing: (value: string) => value ? value : "en" //huh that doesn't make sense -> it's not a required param anyway
    },
    {
        backendName: "inWebJackpots",
        specialParsing: () => "" //just a flag, no value
    },
    {
        backendName: "debug",
    },
    {
        backendName: "gameMode",
    },
    {
        backendName: "operatorId",
    },
    {
        backendName: "gameId", //used here again, but we use it in providerGameId too
        frontendName: "providerGameId",
        specialParsing: (value: string, gameList: GameListType, urlParams: URLSearchParams) => {
            if (urlParams.get("addOperatorIdToGameId") === "true") {
                const originalId = findGame(value, gameList)?.originalId;
                value = originalId || value
            }
            return "BFG" + value;
        }
    },
    {
        backendName: "limitBuyFeatureToMaxBet"
    },
    {
        backendName: "jackpotSessionId",
    },
    {
        backendName: "rciframeurl",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "rchistoryurl",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "homeurl",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "rcinterval",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "rcelapsed",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "showrgdebuglog",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "rcenable",
        specialParsing: (value: string) => encodeURIComponent(value)
    },
    {
        backendName: "extendPlaytechAPISupport"
    },
    {
        backendName: "stage"
    },
    {
        backendName: "jackpotRulesTemplateUrl"
    },
    {
        backendName: "jackpotRulesUrl"
    },
    {
        backendName: "jackpotExtraRulesUrl"
    },
    {
        backendName: "showLowerBetNotification"
    },
    {
        backendName: "hackRulesMaxWinningsCap"
    },
    {
        backendName: "showMaxHourlyLossRules"
    },
    {
        backendName: "exitButtonEnabledInIframes"
    },
    {
        backendName: "skipRevertWinningsPresentation"
    },
    {
        backendName: "showPaytableAtGameStart"
    },
    {
        backendName: "enableRubyJokerBetPolyfill"
    },
    {
        backendName: "showTotalAutoplayCost"
    },
    {
        backendName: "showAutoplayConfirmationWindow"
    }
]

const E2Parameters: Parameter[] = [
    {
        backendName: "gameId",
        required: true
    },
    {
        backendName: "userId",
        required: true
    },
    {
        backendName: "sessionId",
        required: true
    },
    {
        backendName: "gameServerAddress",
        frontendName: "host",
        required: true,
        specialParsing: (value: string) => {
            const host = safeCreateUrl(value)
            return host.hostname + host.pathname
        }
    },
    {
        backendName: "useNovoLoader",
        specialParsing: (value: string) => value === "true" || value === "" ? "true" : "false" //not sure if it comes like this &useNovoLoader=true or just &useNovoLoader tbh
    },
    {
        backendName: "operatorId",

    },
    {
        backendName: "providerGameId",
        specialParsing: (value: string) => "BFG" + value
    },
    {
        backendName: "jackpotSessionId",
    }
]

export function ReturnFrontendParametersString(urlParams: URLSearchParams, engine: "e3" | "e2", gameList: GameListType): string {
    let paramString = ""
    const selectedParameters = engine === "e3" ? E3Parameters : E2Parameters
    selectedParameters.forEach((param) => {
        const backendValue = urlParams.get(param.backendName)
        if (backendValue !== null && backendValue !== undefined) {
            const frontendName = param.frontendName || param.backendName
            const value = param.specialParsing ? param.specialParsing(backendValue, gameList, urlParams) : backendValue
            if (value !== "") {
                paramString += `&${frontendName}=${value}`
            } else {
                paramString += `&${frontendName}`
            }
        } else if (param.required) {
            throw new Error(`Missing required parameter: ${param.backendName}`)
        }
    })
    //remove first "&"
    return paramString.substring(1)
}

export const checkIfMobile = (): boolean => {
    let runMobile = false;
    if (typeof document !== 'undefined') {
        runMobile = document.location.search.includes("forceMobile") || isMobile(navigator?.userAgent, window?.innerWidth || 1, window?.innerHeight || 1);
    }
    return runMobile;
}
